import React, { useEffect, useMemo, useState } from 'react';
import { TEMPO_MAX, TEMPO_MIN, normalizeSongTempo } from '@chordbaker/music-theory';
import { useTheme, Theme } from '../contexts/ThemeContext';
import SectionEditorModalFrame from './SectionEditorModalFrame';

/** What the editor starts from when the song has no tempo yet. */
export const DEFAULT_TEMPO = 120;

const createStyles = (theme: Theme) => ({
  body: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: theme.spacing.xl,
  },
  stepperRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.lg,
  },
  stepButton: {
    width: theme.sizes.hitTarget,
    height: theme.sizes.hitTarget,
    borderRadius: theme.radii.full,
    border: 'none',
    backgroundColor: theme.colors.surfaceSecondary,
    color: theme.colors.text,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.typography.sizes.xl,
    cursor: 'pointer',
  },
  input: {
    width: theme.sizes.button.minWidth,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
    borderRadius: theme.radii.md,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.isDark ? theme.colors.surfaceSecondary : theme.colors.surface,
    fontSize: theme.typography.sizes.xxxl,
    fontWeight: theme.typography.weights.semiBold,
    fontVariantNumeric: 'tabular-nums',
    color: theme.colors.text,
    textAlign: 'center' as const,
  },
  label: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    margin: 0,
  },
});

export interface TempoEditorModalProps {
  isOpen: boolean;
  initialValue: number | null;
  onClose: () => void;
  /** null clears the tempo. */
  onApply: (tempo: number | null) => void;
}

/**
 * Song tempo editor: a BPM field with −/+ steppers, in the same card as the
 * section repeat and note editors. Values clamp into TEMPO_MIN..TEMPO_MAX;
 * applying an empty field clears the tempo.
 */
const TempoEditorModal: React.FC<TempoEditorModalProps> = ({ isOpen, initialValue, onClose, onApply }) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [text, setText] = useState(String(initialValue ?? DEFAULT_TEMPO));

  // Start from the song's current tempo each time the editor opens
  useEffect(() => {
    if (isOpen) {
      setText(String(initialValue ?? DEFAULT_TEMPO));
    }
  }, [isOpen, initialValue]);

  const step = (delta: number) => {
    const current = normalizeSongTempo(Number(text)) ?? initialValue ?? DEFAULT_TEMPO;
    setText(String(normalizeSongTempo(current + delta) ?? current));
  };

  return (
    <SectionEditorModalFrame
      isOpen={isOpen}
      title="Tempo"
      onClose={onClose}
      onApply={() => onApply(normalizeSongTempo(Number(text)))}
      onClear={initialValue != null ? () => onApply(null) : undefined}
    >
      <div style={styles.body}>
        <div style={styles.stepperRow}>
          <button type="button" onClick={() => step(-1)} style={styles.stepButton} aria-label="Slower" title="Slower">
            −
          </button>
          <input
            type="text"
            inputMode="numeric"
            value={text}
            onChange={event => setText(event.target.value.replace(/[^0-9]/g, '').slice(0, String(TEMPO_MAX).length))}
            onFocus={event => event.target.select()}
            style={styles.input}
            aria-label="Beats per minute"
            data-testid="tempo-input"
          />
          <button type="button" onClick={() => step(1)} style={styles.stepButton} aria-label="Faster" title="Faster">
            +
          </button>
        </div>
        <p style={styles.label}>Beats per minute · {TEMPO_MIN}–{TEMPO_MAX}</p>
      </div>
    </SectionEditorModalFrame>
  );
};

export default TempoEditorModal;
